import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import SvgIcon from "components/base/SvgIcon";
import { cn } from "utils";

const Collapsible = ({
  children,
  label,
  count,
  isVisible,
  onToggleCollapse,
  unMountChildrenWhenNotVisible,
  actionComponent,
  className,
  labelClassName,
  highLightLabel,
  disabled,
  hideChevron,
}) => {
  const [visible, setVisible] = useState(isVisible);

  useEffect(() => setVisible(isVisible), [isVisible]);

  const toggleCollapse = () => {
    if (disabled) {
      return;
    }

    setVisible((prev) => !prev);
    onToggleCollapse(visible);
  };

  return (
    <div className={cn(`font-rubik ${className}`)}>
      <div className="w-fit relative">
        <div
          title={disabled ? "No items available" : visible ? "close" : "open"}
          className={cn(
            "flex items-center opacity-80 transition-all select-none w-min",
            {
              "cursor-pointer hover:opacity-100": !disabled,
              "cursor-not-allowed opacity-45": disabled,
              "!opacity-100": visible && !disabled,
            }
          )}
          onClick={toggleCollapse}
        >
          {!hideChevron && (
            <SvgIcon
              type={visible ? "chevronDown" : "chevronRight"}
              className={cn(
                "mr-2 shrink-0 text-slate-700",
                visible ? "h-[7px] w-[10px]" : "h-[10px] w-[7px]"
              )}
            />
          )}
          <div className="flex items-center gap-2">
            <p
              className={cn(
                "font-semibold text-lg whitespace-nowrap text-gray-400 uppercase",
                labelClassName,
                { "bg-[#ffff00] text-gray-900": highLightLabel }
              )}
            >
              {label}
            </p>
            {count !== undefined && (
              <span
                className={cn(
                  "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500",
                  { "bg-slate-50 text-slate-400": disabled }
                )}
              >
                {count}
              </span>
            )}
          </div>
        </div>

        {actionComponent}
      </div>

      <div
        className={cn("mt-2", {
          "h-0 overflow-hidden": !visible,
          "h-auto": visible,
        })}
      >
        {unMountChildrenWhenNotVisible ? visible && children : children}
      </div>
    </div>
  );
};

Collapsible.propTypes = {
  label: PropTypes.string,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isVisible: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  disabled: PropTypes.bool,
  hideChevron: PropTypes.bool,
};

Collapsible.defaultProps = {
  label: "",
  isVisible: false,
  onToggleCollapse: () => {},
  className: "",
  labelClassName: "",
  count: undefined,
  disabled: false,
  hideChevron: false,
};

export default Collapsible;
